import { memo } from 'react';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import { ringSlots, lineSlots, areaSlots } from '../engine/battlefield/deployment';
import styles from '../styles/Battlefield.module.css';

/**
 * WHERE PEOPLE WOULD LAND. While a formation is being drawn the ghosts
 * stand on the line instead of in the ring - the preview and the placement
 * come from the same function, whichever shape is drawn, so what is drawn
 * is what happens (v8.22.0).
 */
function ghostSlots(marker, ghostCount, tileSize, line) {
  if (line) {
    return line.area
      ? areaSlots(line.from, line.to, line.count, tileSize)
      : lineSlots(line.from, line.to, line.count, tileSize);
  }
  return ghostCount > 0 ? ringSlots(marker, ghostCount, marker.ring) : [];
}

/**
 * The reach, as an ellipse: the same projection the footprints use, so it
 * sits on the ground plane rather than facing the viewer.
 */
function ReachRing({ marker, at, height, camera }) {
  const r = marker.ring.radius;
  const east = project({ x: marker.x + r, y: height, z: marker.z }, camera, SCENE_VIEW);
  const north = project({ x: marker.x, y: height, z: marker.z + r }, camera, SCENE_VIEW);
  const rx = Math.hypot(east.x - at.x, east.y - at.y);
  const ry = Math.hypot(north.x - at.x, north.y - at.y);
  return <ellipse cx={at.x} cy={at.y} rx={rx} ry={ry} className={styles.deployRing} />;
}

/** The flag, drawn last so it sits above its own ring. */
function Flag({ at }) {
  return (
    <g transform={`translate(${at.x} ${at.y})`} data-testid="deploy-marker">
      <line x1="0" y1="0" x2="0" y2="-22" className={styles.deployPole} />
      <path d="M0,-21 L14,-16 L0,-11 Z" className={styles.deployFlag} />
      <circle cx="0" cy="0" r="2.5" className={styles.deployFoot} />
    </g>
  );
}

/**
 * The deployment marker, its reach, and where people would land.
 *
 * Drawn only in Deploy mode. The ring is a thing you SEE rather than a
 * number you guess at, and the ghosts answer the question a GM actually
 * has - "where will they end up?" - before anything is written.
 *
 * Purely decorative: `pointer-events: none` throughout, so nothing here
 * can swallow the click that plants the marker. The map's readout learnt
 * that lesson the hard way (gotchas.md, v7.51.5).
 */
function BattlefieldDeployOverlayImpl({ marker, camera, ghostCount = 0, tileSize = 1, line = null }) {
  if (!marker) return null;
  // THE STOREY THE MARKER IS PLANTED ON. `plantAt` has recorded it since
  // v7.94.0 and this drew at y=0 regardless, so a flag on a gantry looked
  // exactly like one on the floor (v7.97.0).
  const y = marker.y ?? 0;
  // WHERE THE BANNER FLIES, and where it is anchored. The ring, its ghosts
  // and the flag all rise together - a flag drawn on the ground while its
  // people land nine metres up would be a picture of the wrong deployment
  // (v8.31.0).
  const air = Math.max(0, marker.air ?? 0);
  const at = project({ x: marker.x, y: y + air, z: marker.z }, camera, SCENE_VIEW);
  const foot = project({ x: marker.x, y, z: marker.z }, camera, SCENE_VIEW);
  return (
    <g className={styles.deployOverlay} aria-hidden="true" data-testid="deploy-overlay">
      <ReachRing marker={marker} at={at} height={y + air} camera={camera} />
      {ghostSlots(marker, ghostCount, tileSize, line).map((slot, i) => {
        const p = project({ x: slot.x, y: (slot.y ?? 0) + air, z: slot.z }, camera, SCENE_VIEW);
        return (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={Math.max(3, tileSize * 2)}
            className={styles.deployGhost}
            data-testid="deploy-ghost"
          />
        );
      })}
      {line && <FormationShape line={line} camera={camera} y={y} />}
      {/* A STALK TO THE GROUND when it is flying, so the height is legible
          and the flag cannot be mistaken for one planted somewhere else. */}
      {air > 0 && (
        <line x1={at.x} y1={at.y} x2={foot.x} y2={foot.y} className={styles.deployStalk} data-testid="deploy-stalk" />
      )}
      <Flag at={at} />
    </g>
  );
}

/**
 * The line being drawn, and how many will stand on it.
 *
 * The count is the whole point of the readout: the room holds six, the
 * line has room for four, and knowing that BEFORE the release is what
 * makes the gesture usable.
 */
function FormationShape({ line, camera, y }) {
  const at = (x, z) => project({ x, y: line.from.y ?? y, z }, camera, SCENE_VIEW);
  const a = at(line.from.x, line.from.z);
  const b = at(line.to.x, line.to.z);
  // A BLOCK IS DRAWN ON THE GROUND, so its outline is the projection of
  // four ground corners - not a screen-space rectangle, which would
  // stand up out of the map at any camera angle but straight down.
  const corners = [
    at(line.from.x, line.from.z),
    at(line.to.x, line.from.z),
    at(line.to.x, line.to.z),
    at(line.from.x, line.to.z),
  ];
  return (
    <g data-testid={line.area ? 'formation-area' : 'formation-line'}>
      {line.area ? (
        <polygon points={corners.map((p) => `${p.x},${p.y}`).join(' ')} className={styles.formationArea} />
      ) : (
        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} className={styles.formationLine} />
      )}
      <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 8} className={styles.formationCount}>
        {line.count} of {line.total ?? line.count}
      </text>
    </g>
  );
}

/**
 * Each team's own arrival point (plan scene-editor-library, B): a flag in the team's livery, its name beside it -
 * drawn in Deploy beside the main marker, decorative like it (`pointer-events: none`).
 *
 * @param {{ points: Array<{ tag: string, label: string, colour: string, point: { x: number, z: number, y?: number } }>,
 *   camera: object }} props
 */
export const TeamArrivalFlags = memo(function TeamArrivalFlags({ points, camera }) {
  if (!points.length) return null;
  return (
    <g className={styles.deployOverlay} aria-hidden="true" data-testid="deploy-team-flags">
      {points.map(({ tag, label, colour, point }) => {
        const at = project({ x: point.x, y: point.y ?? 0, z: point.z }, camera, SCENE_VIEW);
        return (
          <g
            key={tag}
            transform={`translate(${at.x} ${at.y})`}
            style={{ '--livery': colour }}
            data-testid={`deploy-marker-${tag}`}
          >
            <line x1="0" y1="0" x2="0" y2="-22" className={styles.teamArrivalPole} />
            <path d="M0,-21 L14,-16 L0,-11 Z" className={styles.teamArrivalFlag} />
            <text x="17" y="-13" className={styles.teamArrivalLabel}>
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
});

export default memo(BattlefieldDeployOverlayImpl);
